// Types and helpers for GET /api/v1/_schema (the entity registry as served to the UI and to agents).
// The web app never imports server packages, so these types mirror packages/schema/src/registry.ts describeEntity().
import { type EnumLabels, enumLabelsOf } from './format'

export type FieldKind =
  | 'text'
  | 'longtext'
  | 'markdown'
  | 'email'
  | 'url'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'int'
  | 'number'
  | 'json'
  | 'tags'
  | 'file'
  | 'enum'
  | 'ref'
  | 'refs'

/** agent: anyone with write access; human: people only (agents get a 403); system: computed, read-only. */
export type FieldAccess = 'agent' | 'human' | 'system'

export type NavGroup = 'Home' | 'Compliance' | 'Risk' | 'Governance' | 'Trust' | 'Settings'

export interface FieldSchema {
  name: string
  kind: FieldKind
  description: string
  label: string
  required: boolean
  unique: boolean
  default: unknown
  access: FieldAccess
  list: boolean
  filter: boolean
  search: boolean
  sensitive: boolean
  values?: string[]
  /** Enum display labels that differ from the humanized value (Drata wording): NEEDS_RENEWAL "Upcoming renewal". */
  labels?: EnumLabels
  to?: string
}

export interface EntityLinkSchema {
  name: string
  description: string
  direction: 'from' | 'to'
  other: string
}

/**
 * Per-caller permissions served by /_schema (canRead, canCreate, canWrite, canArchive, canRestore, writableFields,
 * creatableFields). When absent (older server, tests) the UI mirrors the server's role rules in lib/permissions.ts.
 */
export interface EntityPermissions {
  read?: boolean
  create?: boolean
  update?: boolean
  archive?: boolean
  /**
   * May restore archived rows. Differs from archive: an archive can be final (compliance exclusions, restorable false),
   * and a permanent entity (controls, archivable false) still restores rows archived before the rule.
   */
  restore?: boolean
  /** Fields the caller may change on existing rows. */
  writableFields?: string[]
  /** Fields the caller may set on create. */
  creatableFields?: string[]
}

export interface EntitySchema {
  name: string
  plural: string
  label: string
  labelPlural: string
  module: string
  nav: { group: NavGroup; order: number; label?: string } | null
  description: string
  display: string
  key: string | null
  defaultSort: string | null
  immutable: boolean
  humanCreateOnly: boolean
  systemCreateOnly: boolean
  agentArchive: boolean
  fields: FieldSchema[]
  links: EntityLinkSchema[]
  permissions?: EntityPermissions
}

export interface LinkSchema {
  name: string
  description: string
  from: string
  to: string
  module: string
  /** Per-caller: may read both sides / may map and unmap. Absent when the server does not send it. */
  permissions?: { canRead?: boolean; canLink?: boolean }
}

export interface Schema {
  entities: EntitySchema[]
  links: LinkSchema[]
}

/** A record as returned by the generic REST API: declared fields plus the system columns. */
export interface RecordRow {
  id: string
  version: number
  createdAt: string
  updatedAt: string
  createdBy: string | null
  updatedBy: string | null
  archivedAt: string | null
  archivedBy: string | null
  sourceSystem: string | null
  sourceId: string | null
  [field: string]: unknown
}

const FIELD_KINDS: readonly FieldKind[] = [
  'text',
  'longtext',
  'markdown',
  'email',
  'url',
  'date',
  'datetime',
  'boolean',
  'int',
  'number',
  'json',
  'tags',
  'file',
  'enum',
  'ref',
  'refs',
]

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback
}

export function humanizeName(s: string): string {
  const spaced = s.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function normalizeField(raw: any): FieldSchema | null {
  if (!raw || typeof raw.name !== 'string') return null
  const kind: FieldKind = FIELD_KINDS.includes(raw.kind) ? raw.kind : 'text'
  const access: FieldAccess = raw.access === 'human' || raw.access === 'system' ? raw.access : 'agent'
  return {
    name: raw.name,
    kind,
    description: str(raw.description),
    label: str(raw.label) || humanizeName(raw.name),
    required: !!raw.required,
    unique: !!raw.unique,
    default: raw.default ?? null,
    access,
    list: !!raw.list,
    filter: !!raw.filter,
    search: !!raw.search,
    sensitive: !!raw.sensitive,
    values: Array.isArray(raw.values) ? raw.values.map(String) : undefined,
    labels: kind === 'enum' ? enumLabelsOf(raw) : undefined,
    to: typeof raw.to === 'string' ? raw.to : kind === 'file' ? 'file' : undefined,
  }
}

function bool(...vals: unknown[]): boolean | undefined {
  for (const v of vals) if (typeof v === 'boolean') return v
  return undefined
}

function strings(v: unknown): string[] | undefined {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : undefined
}

/**
 * Accepts the server's { canRead, canCreate, canWrite, canArchive, canRestore, writableFields, creatableFields } or
 * { read, ... }.
 */
function normalizePermissions(p: any, canCreate?: unknown): EntityPermissions | undefined {
  if (p && typeof p === 'object') {
    return {
      read: bool(p.canRead, p.read),
      create: bool(p.canCreate, p.create),
      update: bool(p.canWrite, p.canUpdate, p.update),
      archive: bool(p.canArchive, p.archive),
      restore: bool(p.canRestore, p.restore),
      writableFields: strings(p.writableFields),
      creatableFields: strings(p.creatableFields),
    }
  }
  return typeof canCreate === 'boolean' ? { create: canCreate } : undefined
}

function normalizeEntity(raw: any): EntitySchema | null {
  if (!raw || typeof raw.name !== 'string' || typeof raw.plural !== 'string') return null
  const fields = (Array.isArray(raw.fields) ? raw.fields : [])
    .map(normalizeField)
    .filter(Boolean) as FieldSchema[]
  const nav =
    raw.nav && typeof raw.nav === 'object' && typeof raw.nav.group === 'string'
      ? {
          group: raw.nav.group as NavGroup,
          order: Number(raw.nav.order) || 0,
          label: raw.nav.label ?? undefined,
        }
      : null
  const permissions = normalizePermissions(raw.permissions, raw.canCreate)
  return {
    name: raw.name,
    plural: raw.plural,
    label: str(raw.label) || humanizeName(raw.name),
    labelPlural: str(raw.labelPlural) || str(raw.label) || humanizeName(raw.plural),
    module: str(raw.module, 'core'),
    nav,
    description: str(raw.description),
    display: str(raw.display) || fields[0]?.name || 'id',
    key: typeof raw.key === 'string' ? raw.key : null,
    defaultSort: typeof raw.defaultSort === 'string' ? raw.defaultSort : null,
    immutable: !!raw.immutable,
    humanCreateOnly: !!raw.humanCreateOnly,
    systemCreateOnly: !!raw.systemCreateOnly,
    agentArchive: raw.agentArchive !== false,
    fields,
    links: (Array.isArray(raw.links) ? raw.links : [])
      .filter((l: any) => l && typeof l.name === 'string' && typeof l.other === 'string')
      .map((l: any) => ({
        name: l.name,
        description: str(l.description),
        direction: l.direction === 'to' ? 'to' : 'from',
        other: l.other,
      })),
    permissions,
  }
}

/** Defensive parse of the /_schema payload so a partial or newer server response never crashes the shell. */
export function normalizeSchema(raw: unknown): Schema {
  const r = (raw && typeof raw === 'object' ? raw : {}) as any
  const body = r.data && typeof r.data === 'object' && !Array.isArray(r.data) && r.data.entities ? r.data : r
  const entities = (Array.isArray(body.entities) ? body.entities : [])
    .map(normalizeEntity)
    .filter(Boolean) as EntitySchema[]
  const links: LinkSchema[] = (Array.isArray(body.links) ? body.links : [])
    .filter((l: any) => l && typeof l.name === 'string')
    .map((l: any) => ({
      name: l.name,
      description: str(l.description),
      from: str(l.from),
      to: str(l.to),
      module: str(l.module, 'core'),
      permissions:
        l.permissions && typeof l.permissions === 'object'
          ? { canRead: bool(l.permissions.canRead), canLink: bool(l.permissions.canLink) }
          : undefined,
    }))
  return { entities, links }
}

/** Fast lookups over a normalized schema. */
export class SchemaIndex {
  readonly entities: EntitySchema[]
  readonly links: LinkSchema[]
  private byName = new Map<string, EntitySchema>()
  private byPluralMap = new Map<string, EntitySchema>()
  private linkByName = new Map<string, LinkSchema>()

  constructor(schema: Schema) {
    this.entities = schema.entities
    this.links = schema.links
    for (const e of schema.entities) {
      this.byName.set(e.name, e)
      this.byPluralMap.set(e.plural, e)
    }
    for (const l of schema.links) this.linkByName.set(l.name, l)
  }

  entity(name: string): EntitySchema | undefined {
    return this.byName.get(name)
  }

  byPlural(plural: string): EntitySchema | undefined {
    return this.byPluralMap.get(plural)
  }

  /** Accepts an entity name or a plural. */
  find(nameOrPlural: string): EntitySchema | undefined {
    return this.byName.get(nameOrPlural) ?? this.byPluralMap.get(nameOrPlural)
  }

  link(name: string): LinkSchema | undefined {
    return this.linkByName.get(name)
  }
}

export function fieldOf(entity: EntitySchema, name: string): FieldSchema | undefined {
  return entity.fields.find((f) => f.name === name)
}

/** The enum field shown as the record's status badge: status, then readiness, then any enum ending in "status". */
export function statusFieldOf(entity: EntitySchema): FieldSchema | undefined {
  const enums = entity.fields.filter((f) => f.kind === 'enum')
  return (
    enums.find((f) => f.name === 'status') ??
    enums.find((f) => f.name === 'readiness') ??
    enums.find((f) => /status$/i.test(f.name) && f.list)
  )
}

export function detailPath(entity: Pick<EntitySchema, 'plural'>, id: string): string {
  return `/${entity.plural}/${encodeURIComponent(id)}`
}
